// src/utils/sentry.ts
// Sentry configuration for Vue frontend error tracking
// Initializes Sentry with environment detection and privacy settings
// RELEVANT FILES: src/main.ts, src/stores/auth.ts

import * as Sentry from '@sentry/vue';
import type { App } from 'vue';
import type { Router } from 'vue-router';

import { stage } from '@/config/env';

/**
 * Environment detection based on hostname
 */
export function getSentryEnvironment(): {
  environment: 'prod' | 'pre' | 'test' | 'development';
  shouldEnable: boolean;
} {
  const hostname = window.location.hostname;

  const isProduction = hostname === 'portal.retaintive.ai';
  const isPre = hostname === 'portal-pre.retaintive.ai';
  const isTest = hostname === 'portal-test.retaintive.ai';
  const shouldEnable = isProduction || isPre || isTest;

  return {
    environment: isProduction ? 'prod' : isPre ? 'pre' : isTest ? 'test' : 'development',
    shouldEnable,
  };
}

/**
 * Set Sentry user context with authenticated user information
 * Call this after user logs in to pre-fill feedback forms
 */
export function setSentryUser(
  cognitoSub: string | undefined,
  email: string | undefined,
  username: string | undefined,
): void {
  Sentry.setUser({
    id: cognitoSub, // Cognito sub as permanent user ID
    email: email || undefined,
    username: username || undefined,
  });
}

/**
 * Clear Sentry user context (call on logout)
 */
export function clearSentryUser(): void {
  Sentry.setUser(null);
}

/**
 * Initialize Sentry for Vue application
 * Only enables on deployed domains (production, pre, test)
 * Disabled for localhost to prevent local error noise
 */
export function initializeSentry(app: App, router: Router): void {
  const { environment, shouldEnable } = getSentryEnvironment();

  if (!shouldEnable) {
    console.log(`Sentry disabled for localhost (hostname: ${window.location.hostname})`);
    return;
  }

  const isProduction = environment === 'prod';

  Sentry.init({
    app,
    // Note: DSN is public and safe to commit - it only allows sending errors, not reading them
    dsn: 'https://077c529b0b24c26c69bcda31e454e1af@o4510203520745472.ingest.us.sentry.io/4510204286074880',

    // Set environment based on hostname
    environment,

    // Set release version for source map matching (uses stage from config)
    release: stage || 'development',

    // Performance monitoring - 10% in prod, 100% in test/pre
    // Free tier: 10,000 transactions/month - 10% sampling keeps us well within limits
    tracesSampleRate: isProduction ? 0.1 : 1.0,

    // Session replay sampling - OPTIMIZED FOR FREE TIER
    // Free tier: 500 replay sessions/month
    // 5% base sampling + 100% on errors = ~500 sessions with 1k users
    replaysSessionSampleRate: isProduction ? 0.05 : 0.5, // Reduced from 0.1 to 0.05
    replaysOnErrorSampleRate: 1.0, // Always capture replay on error

    // Track Vue router navigation and user feedback
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({
        maskAllText: true, // Mask all text for privacy
        blockAllMedia: true, // Block all media for privacy
      }),
      Sentry.feedbackIntegration({
        // Match system color scheme (light/dark mode)
        colorScheme: 'system',
        // Show crash-report modal only on errors (no floating button)
        autoInject: false,
        // Show name and email fields (will be pre-filled when user is authenticated)
        showName: true,
        showEmail: true,
        // Enable screenshot attachment
        enableScreenshot: true,
        // Show feedback dialog on error events
        triggerLabel: 'Report Feedback',
        triggerAriaLabel: 'Report Feedback',
      }),
    ],

    // Filter out browser extension errors and common noise
    // Free tier: 5,000 error events/month - aggressive filtering protects quota
    ignoreErrors: [
      // Common benign browser errors
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      'Non-Error exception captured',

      // Browser extension errors (not actionable)
      'chrome-extension://',
      'moz-extension://',
      'safari-extension://',

      // Network errors (often not actionable)
      'Failed to fetch',
      'NetworkError',
      'Network request failed',

      // Common third-party script errors
      'Script error.',
      'Script error',
    ],

    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,

    // Filter out sensitive data and low-value errors before sending to Sentry
    beforeSend(event, hint) {
      // Remove any potential auth tokens from URLs
      if (event.request?.url) {
        event.request.url = event.request.url.replace(/token=[^&]+/gi, 'token=[REDACTED]');
      }

      // Remove Authorization headers
      if (event.request?.headers) {
        delete event.request.headers.Authorization;
        delete event.request.headers.authorization;
      }

      // Additional filtering for free tier quota protection
      const error = hint.originalException as Error;

      // Filter out errors that match ignore patterns (belt and suspenders with ignoreErrors)
      if (error?.message) {
        const ignorePatterns = [
          /ResizeObserver/i,
          /chrome-extension/i,
          /moz-extension/i,
          /Failed to fetch/i,
          /NetworkError/i,
        ];

        if (ignorePatterns.some(pattern => pattern.test(error.message))) {
          return null; // Don't send to Sentry
        }
      }

      return event;
    },
  });

  console.log(`Sentry initialized for environment: ${environment}`);
}
